import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, ChevronDown, ChevronUp } from 'lucide-react';
import InstallPWA from '../components/InstallPWA';
import API_BASE_URL from '../config/api';

interface AnswerChoice {
  choice_letter: string;
  choice_text: string;
}

interface Question {
  id: number;
  question_number: number;
  number_of_answers: number;
  question_text?: string;
  points?: number;
  answer_choices?: AnswerChoice[];
  has_multiple_correct_answers?: boolean;
  student_answer?: number | number[];
  part_id?: number;
}

interface part {
  id: number;
  titre: string;
  ordre: number;
}

interface Test {
  id: number;
  title: string;
  total_questions: number;
  type?: string;
  statut_id: number;
  parts?: part[];
}

interface StudentAnswers {
  [questionId: number]: number | number[];
}

const StudentAnswerTest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [parts, setparts] = useState<part[]>([]);
  const [currentpartIndex, setCurrentpartIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [studentAnswers, setStudentAnswers] = useState<StudentAnswers>({});
  const [showAnsweredQuestions, setShowAnsweredQuestions] = useState<boolean>(false);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [skippedQuestions, setSkippedQuestions] = useState<Set<number>>(new Set());
  const [showSkippedQuestions, setShowSkippedQuestions] = useState<boolean>(false);
  const [animatingQuestionId, setAnimatingQuestionId] = useState<number | null>(null);
  const [firstName, setFirstName] = useState<string>(() => {
    return localStorage.getItem('studentFirstName') || '';
  });
  const [lastName, setLastName] = useState<string>(() => {
    return localStorage.getItem('studentLastName') || '';
  });
  const [studentId, setStudentId] = useState<number | null>(() => {
    const savedId = localStorage.getItem('studentId');
    return savedId ? parseInt(savedId, 10) : null;
  });
  const [isNameValidated, setIsNameValidated] = useState<boolean>(() => {
    return localStorage.getItem('isNameValidated') === 'true';
  });
  const [isCreatingStudent, setIsCreatingStudent] = useState<boolean>(false);

  // Couleurs pour les boutons de réponses (gris par défaut / couleur quand sélectionné)
  const answerColors = [
    'bg-blue-500 hover:bg-blue-600',
    'bg-green-500 hover:bg-green-600',
    'bg-yellow-500 hover:bg-yellow-600',
    'bg-red-500 hover:bg-red-600',
    'bg-purple-500 hover:bg-purple-600',
    'bg-pink-500 hover:bg-pink-600',
    'bg-indigo-500 hover:bg-indigo-600',
    'bg-orange-500 hover:bg-orange-600',
    'bg-teal-500 hover:bg-teal-600',
    'bg-cyan-500 hover:bg-cyan-600'
  ];

  // Convertir un nombre en lettre (1 -> A, 2 -> B, etc.)
  const numberToLetter = (num: number): string => {
    return String.fromCharCode(64 + num); // 65 = A
  };

  useEffect(() => {
    fetchTestAndQuestions();
  }, [testId]);

  // Effet pour charger les réponses existantes quand studentId est disponible
  useEffect(() => {
    if (studentId && testId) {
      fetchExistingAnswers();
    }
  }, [studentId, testId]);

  // Effet pour synchroniser les changements de nom/prénom avec localStorage
  useEffect(() => {
    if (isNameValidated) {
      localStorage.setItem('studentFirstName', firstName);
      localStorage.setItem('studentLastName', lastName);
    }
  }, [firstName, lastName, isNameValidated]);

  const fetchTestAndQuestions = async (): Promise<void> => {
    try {
      // Récupérer le test et ses questions (route publique)
      const testResponse = await fetch(`${API_BASE_URL}/api/tests/${testId}/for-student`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!testResponse.ok) {
        throw new Error(t('studentAnswerTest.testNotFound'));
      }

      const testData = await testResponse.json();
      setTest(testData.test);
      setQuestions(testData.test.questions);
      setparts(testData.test.parts || []);
      setLoading(false);

    } catch (error) {
      console.error(t('common.error'), error);
      setLoading(false);
    }
  };

  const fetchExistingAnswers = async (): Promise<void> => {
    try {
      console.log('🔍 Chargement des réponses existantes pour l\'étudiant:', studentId, 'Test:', testId);

      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}/student-answers-public/${studentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Si l'étudiant n'a pas encore de réponses, ce n'est pas une erreur
        if (response.status === 404) {
          console.log('ℹ️ Aucune réponse existante trouvée');
          return;
        }
        throw new Error('Erreur lors de la récupération des réponses existantes');
      }

      const data = await response.json();
      console.log('✅ Réponses existantes récupérées:', data.answers);

      // Convertir le tableau de réponses en objet {questionId: answer}
      const answersMap: StudentAnswers = {};
      data.answers.forEach((item: { question_id: number; answer: string | number }) => {
        let parsedAnswer: number | number[];

        // Parser la réponse (maintenant stockée en TEXT)
        if (typeof item.answer === 'string') {
          // Si c'est un JSON array (réponses multiples)
          if (item.answer.startsWith('[')) {
            try {
              parsedAnswer = JSON.parse(item.answer);
            } catch {
              parsedAnswer = parseInt(item.answer);
            }
          } else {
            // Réponse simple stockée comme "1", "2", etc.
            parsedAnswer = parseInt(item.answer);
          }
        } else {
          // Compatibilité avec l'ancien format (number)
          parsedAnswer = item.answer;
        }

        answersMap[item.question_id] = parsedAnswer;
      });

      // Mettre à jour l'état avec les réponses existantes
      setStudentAnswers(answersMap);

      // Mettre à jour les questions avec les réponses existantes
      setQuestions(prev =>
        prev.map(q =>
          answersMap[q.id] !== undefined
            ? { ...q, student_answer: answersMap[q.id] }
            : q
        )
      );

    } catch (error) {
      console.error('❌ Erreur lors du chargement des réponses existantes:', error);
      // Ne pas afficher d'alerte car c'est optionnel
    }
  };

  const handleValidateName = async (): Promise<void> => {
    if (!firstName.trim() || !lastName.trim()) {
      return;
    }

    setIsCreatingStudent(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/create-student`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim()
        })
      });

      if (!response.ok) {
        throw new Error(t('studentAnswerTest.creationError'));
      }

      const data = await response.json();
      const newStudentId = data.student.id;

      // Sauvegarder dans localStorage
      localStorage.setItem('studentId', newStudentId.toString());
      localStorage.setItem('studentFirstName', firstName.trim());
      localStorage.setItem('studentLastName', lastName.trim());
      localStorage.setItem('isNameValidated', 'true');

      setStudentId(newStudentId);
      setIsNameValidated(true);
      console.log('✅ Étudiant créé avec ID:', newStudentId);

    } catch (error) {
      console.error('❌ ' + t('studentAnswerTest.creationError'), error);
    } finally {
      setIsCreatingStudent(false);
    }
  };

  const handleChangeName = (): void => {
    // Supprimer du localStorage
    localStorage.removeItem('studentId');
    localStorage.removeItem('studentFirstName');
    localStorage.removeItem('studentLastName');
    localStorage.removeItem('isNameValidated');

    setIsNameValidated(false);
    setStudentId(null);
    setFirstName('');
    setLastName('');
  };

  const handleAnswerSelect = async (questionId: number, answerNumber: number): Promise<void> => {
    // Vérifier que le nom et prénom sont validés
    if (!isNameValidated || !studentId) {
      return;
    }

    setSaving(true);

    try {
      const url = `${API_BASE_URL}/api/tests/save-student-answer`;
      console.log('🔍 Tentative de sauvegarde de la réponse:');
      console.log('   URL:', url);
      console.log('   Question ID:', questionId);
      console.log('   Réponse:', answerNumber);
      console.log('   Étudiant:', firstName, lastName);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          answer: answerNumber,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          studentId: studentId
        })
      });

      console.log('📡 Statut de la réponse:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: t('studentAnswerTest.unknownError') }));
        console.error('❌ ' + t('common.error'), errorData);
        throw new Error(errorData.error || t('studentAnswerTest.saveError'));
      }

      const data = await response.json();
      console.log('✅ Réponse sauvegardée avec succès:', data);

      // Sauvegarder le studentId pour les prochaines réponses
      if (data.studentAnswer && data.studentAnswer.student_id && !studentId) {
        setStudentId(data.studentAnswer.student_id);
      }

      // Déclencher l'animation de disparition (mobile uniquement)
      setAnimatingQuestionId(questionId);

      // Attendre la fin de l'animation avant de mettre à jour l'état
      setTimeout(() => {
        // Mettre à jour l'état local
        setStudentAnswers(prev => ({
          ...prev,
          [questionId]: answerNumber
        }));

        setQuestions(prev =>
          prev.map(q =>
            q.id === questionId
              ? { ...q, student_answer: answerNumber }
              : q
          )
        );

        // Réinitialiser l'animation
        setAnimatingQuestionId(null);
      }, 500); // Durée de l'animation en ms

    } catch (error) {
      console.error('❌ ' + t('studentAnswerTest.saveError'), error);
    } finally {
      setSaving(false);
    }
  };

  // Gestion de passage de question
  const handleSkipQuestion = async (questionId: number): Promise<void> => {
    if (!isNameValidated || !studentId) {
      return;
    }

    setSaving(true);

    try {
      const url = `${API_BASE_URL}/api/tests/save-student-answer`;
      console.log('⏭️ Passage de la question:', questionId);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          answer: -1, // -1 indique que la question est passée
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          studentId: studentId
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du passage de la question');
      }

      console.log('✅ Question passée enregistrée');

      // Ajouter à la liste des questions passées
      const newSkipped = new Set(skippedQuestions);
      newSkipped.add(questionId);
      setSkippedQuestions(newSkipped);

    } catch (error) {
      console.error('❌ Erreur lors du passage de la question:', error);
    } finally {
      setSaving(false);
    }
  };

  // Gestion des réponses multiples (toggle avec sauvegarde automatique)
  const handleMultipleAnswerToggle = async (questionId: number, answerNumber: number): Promise<void> => {
    if (!isNameValidated || !studentId) {
      return;
    }

    // Calculer les nouvelles réponses
    const currentAnswers = studentAnswers[questionId];
    let newAnswers: number[];

    if (Array.isArray(currentAnswers)) {
      // Toggle: ajouter ou retirer la réponse
      if (currentAnswers.includes(answerNumber)) {
        newAnswers = currentAnswers.filter(a => a !== answerNumber);
      } else {
        newAnswers = [...currentAnswers, answerNumber].sort();
      }
    } else {
      // Première sélection: créer un tableau
      newAnswers = [answerNumber];
    }

    // Mettre à jour l'état local immédiatement pour une meilleure UX
    const updatedAnswers = newAnswers.length > 0 ? newAnswers : undefined;
    setStudentAnswers(prev => {
      const updated = { ...prev };
      if (updatedAnswers === undefined) {
        delete updated[questionId];
      } else {
        updated[questionId] = updatedAnswers;
      }
      return updated;
    });

    // Sauvegarder automatiquement dans la base de données
    setSaving(true);
    try {
      const url = `${API_BASE_URL}/api/tests/save-student-answer`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          answer: newAnswers.length > 0 ? JSON.stringify(newAnswers) : null,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          studentId: studentId
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde');
      }

      if (newAnswers.length > 0) {
        console.log('✅ Réponses multiples sauvegardées automatiquement');
      } else {
        console.log('✅ Réponse supprimée automatiquement');
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      // Restaurer l'état précédent en cas d'erreur
      setStudentAnswers(prev => ({
        ...prev,
        [questionId]: currentAnswers
      }));
    } finally {
      setSaving(false);
    }
  };


  // Filtrer les questions par part
  const currentpart = parts[currentpartIndex];
  const currentpartQuestions = currentpart
    ? questions.filter(q => q.part_id === currentpart.id)
    : questions; // Si pas de parts, afficher toutes les questions

  // Vérifier si toutes les questions de la part courante ont une réponse ou sont passées
  const currentpartAnswersFilled = currentpartQuestions.every(q => studentAnswers[q.id] !== undefined || skippedQuestions.has(q.id));

  // Vérifier si toutes les questions du test ont une réponse ou sont passées
  const allAnswersFilled = questions.every(q => studentAnswers[q.id] !== undefined || skippedQuestions.has(q.id));

  // Vérifier s'il y a une part suivante
  const hasNextpart = currentpartIndex < parts.length - 1;

  // Déterminer si on peut valider (dernière part ou pas de parts)
  const canValidate = (!currentpart || !hasNextpart) && allAnswersFilled;

  const handleNextpart = (): void => {
    if (hasNextpart) {
      setCurrentpartIndex(prev => prev + 1);
      window.scrollTo(0, 0); // Remonter en haut de la page
    }
  };

  const handlePreviouspart = (): void => {
    if (currentpartIndex > 0) {
      setCurrentpartIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleValidate = (): void => {
    const confirmed = window.confirm(
      t('studentAnswerTest.validateConfirm')
    );

    if (!confirmed) return;

    // Les réponses sont déjà enregistrées dans la base de données
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('studentAnswerTest.loading')}</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('studentAnswerTest.testNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header - Optimisé pour mobile */}
      <header className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800 truncate">
            {test.title}
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-12">
        <div className="bg-white rounded-lg shadow-2xl p-4 sm:p-8">
          {/* Message si le test n'est pas en cours */}
          {test.statut_id !== 3 ? (
            <div className="text-center py-12">
              <div className="mb-4">
                <svg className="mx-auto h-16 w-16 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('studentAnswerTest.testNotAvailable')}</h2>
              <p className="text-gray-600 text-lg">
                {t('studentAnswerTest.testNotAvailableMessage')}
              </p>
            </div>
          ) : (
            <>
              {/* Champs Nom et Prénom */}
              <div className={`mb-6 bg-blue-50 rounded-lg border-2 border-blue-200 transition-all duration-300 ${
                isNameValidated ? 'p-2' : 'p-4'
              }`}>
            <h3 className={`font-bold text-gray-800 ${isNameValidated ? 'text-sm mb-0' : 'text-lg mb-3'}`}>
              {t('studentAnswerTest.studentIdentification')}
            </h3>

            {!isNameValidated ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('studentAnswerTest.lastName')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('studentAnswerTest.lastNamePlaceholder')}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-base"
                  disabled={isNameValidated}
                />
              </div>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('studentAnswerTest.firstName')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('studentAnswerTest.firstNamePlaceholder')}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-base"
                  disabled={isNameValidated}
                />
              </div>
            </div>

            {/* Boutons de validation et modification */}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleValidateName}
                disabled={!firstName.trim() || !lastName.trim() || isCreatingStudent}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition ${
                  firstName.trim() && lastName.trim() && !isCreatingStudent
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Check className="w-5 h-5" />
                {isCreatingStudent ? t('studentAnswerTest.validating') : t('studentAnswerTest.validateIdentity')}
              </button>
            </div>

            <p className="text-sm text-orange-600 mt-3">
              {t('studentAnswerTest.validateNameWarning')}
            </p>
              </>
            ) : (
              // Version collapsed quand validé
              <div className="flex items-center gap-2 text-green-600 mt-2">
                <Check className="w-4 h-4" />
                <span className="font-semibold text-sm">{firstName} {lastName}</span>
              </div>
            )}
          </div>

          <div className="mb-4 sm:mb-6">
            {currentpart ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {currentpart.titre}
                  </h2>
                  <span className="text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded-lg">
                    {t('studentAnswerTest.part')} {currentpartIndex + 1} {t('studentAnswerTest.of')} {parts.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm sm:text-base text-gray-600">
                    {t('studentAnswerTest.selectAnswerInstruction')}
                  </p>
                  <div className="md:hidden bg-blue-100 px-3 py-1 rounded-full">
                    <span className="text-sm font-bold text-blue-800">
                      {currentpartQuestions.filter(q => studentAnswers[q.id] !== undefined).length}/{currentpartQuestions.length}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {t('studentAnswerTest.selectYourAnswers')}
                  </h2>
                  <div className="md:hidden bg-blue-100 px-3 py-1 rounded-full">
                    <span className="text-sm font-bold text-blue-800">
                      {Object.keys(studentAnswers).length}/{questions.length}
                    </span>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-600">
                  {t('studentAnswerTest.selectAnswerInstruction')}
                </p>
              </>
            )}
          </div>

          {/* Vue pour tests détaillés */}
          {test.type === 'detailed' && (
            <div className="space-y-4">
              {currentpartQuestions.map((q: Question) => {
                const isAnswered = studentAnswers[q.id] !== undefined && studentAnswers[q.id] !== -1;
                const isSkipped = studentAnswers[q.id] === -1 || skippedQuestions.has(q.id);

                return (
                  <div
                    key={q.id}
                    className={`border-2 rounded-lg p-4 transition ${
                      isAnswered
                        ? 'border-green-500 bg-green-50'
                        : isSkipped
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-gray-300 bg-white'
                    }`}
                  >
                    {/* Question title */}
                    <div className="mb-4">
                      <h3 className="font-bold text-lg text-gray-800 mb-1">
                        Question {q.question_number}
                      </h3>
                      {q.question_text && (
                        <p className="text-gray-700">{q.question_text}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {q.points && q.points > 1 && (
                          <p className="text-sm text-gray-600">
                            ({q.points} points)
                          </p>
                        )}
                        {q.has_multiple_correct_answers && (
                          <p className="text-sm text-gray-600 ml-auto">
                            (Plusieurs réponses possibles)
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Answer choices */}
                    <div className="space-y-2 mb-3">
                      {q.answer_choices && q.answer_choices.map((choice: AnswerChoice, idx: number) => {
                        const answerNumber = idx + 1;
                        const currentAnswers = studentAnswers[q.id];
                        const isSelected = Array.isArray(currentAnswers)
                          ? currentAnswers.includes(answerNumber)
                          : currentAnswers === answerNumber;

                        if (q.has_multiple_correct_answers) {
                          // Affichage avec checkboxes pour questions à choix multiples
                          return (
                            <button
                              key={choice.choice_letter}
                              onClick={() => handleMultipleAnswerToggle(q.id, answerNumber)}
                              disabled={saving}
                              className={`w-full text-left p-3 border-2 rounded-lg transition flex items-start gap-3 ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-50 font-semibold'
                                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}} // Géré par le onClick du bouton
                                className="mt-1 w-5 h-5 text-blue-600 rounded pointer-events-none"
                              />
                              <div className="flex-1">
                                <span className="font-bold text-blue-600 mr-2">
                                  {choice.choice_letter}.
                                </span>
                                <span className={isSelected ? 'text-blue-900' : 'text-gray-700'}>
                                  {choice.choice_text}
                                </span>
                              </div>
                            </button>
                          );
                        } else {
                          // Affichage standard avec bouton pour choix unique
                          return (
                            <button
                              key={choice.choice_letter}
                              onClick={() => handleAnswerSelect(q.id, answerNumber)}
                              disabled={saving}
                              className={`w-full text-left p-3 border-2 rounded-lg transition ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-50 font-semibold'
                                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className="font-bold text-blue-600 mr-2">
                                {choice.choice_letter}.
                              </span>
                              <span className={isSelected ? 'text-blue-900' : 'text-gray-700'}>
                                {choice.choice_text}
                              </span>
                              {isSelected && (
                                <Check className="inline-block ml-2 w-5 h-5 text-green-600" />
                              )}
                            </button>
                          );
                        }
                      })}
                    </div>

                    {/* Bouton Passer ou Indicateur */}
                    {!isAnswered && !isSkipped && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSkipQuestion(q.id)}
                          disabled={saving}
                          className={`text-sm text-orange-600 hover:text-orange-800 font-medium bg-orange-50 hover:bg-orange-100 px-4 py-2 rounded-lg transition ${
                            saving ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {t('studentAnswerTest.skip')}
                        </button>
                      </div>
                    )}

                    {/* Indicateur question passée */}
                    {isSkipped && (
                      <div className="flex justify-center">
                        <span className="text-sm text-orange-700 font-semibold bg-orange-100 px-4 py-2 rounded-lg">
                          Question passée - Cliquez sur une réponse pour répondre
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Vue Mobile: Liste verticale (< 768px) - Tests simples uniquement */}
          {test.type !== 'detailed' && (
          <div className="block md:hidden space-y-3">
            {(() => {
              // Séparer les questions répondues des autres (seulement pour la part courante)
              const answeredQuestions = currentpartQuestions.filter(q => studentAnswers[q.id] !== undefined);
              const unansweredQuestions = currentpartQuestions.filter(q => studentAnswers[q.id] === undefined);
              const currentQuestion = unansweredQuestions[0];

              return (
                <>
                  {/* Bloc des questions répondues (pliable) */}
                  {answeredQuestions.length > 0 && (
                    <div className="border-2 border-green-300 bg-green-50 rounded-lg overflow-hidden">
                      {/* Header cliquable avec chevron */}
                      <button
                        onClick={() => setShowAnsweredQuestions(!showAnsweredQuestions)}
                        className="w-full flex items-center justify-between p-3 hover:bg-green-100 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-green-800">
                            {t('studentAnswerTest.answeredQuestions')} ({answeredQuestions.length})
                          </span>
                        </div>
                        {showAnsweredQuestions ? (
                          <ChevronUp className="w-5 h-5 text-green-700" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-green-700" />
                        )}
                      </button>

                      {/* Contenu pliable - Questions en badges horizontaux */}
                      {showAnsweredQuestions && (
                        <div className="p-3 border-t border-green-200">
                          <div className="flex flex-wrap gap-2">
                            {answeredQuestions.map((q) => (
                              <button
                                key={q.id}
                                onClick={() => setEditingQuestionId(editingQuestionId === q.id ? null : q.id)}
                                className="inline-flex items-center gap-1 bg-white border-2 border-green-400 rounded-lg px-3 py-2 hover:bg-green-100 transition shadow-sm"
                              >
                                <span className="text-sm font-bold text-gray-700">
                                  Q{q.question_number}
                                </span>
                                <span className="text-xs text-green-700 font-semibold">
                                  → {Array.isArray(studentAnswers[q.id])
                                      ? (studentAnswers[q.id] as number[]).map(a => numberToLetter(a)).join(', ')
                                      : numberToLetter(studentAnswers[q.id] as number)}
                                </span>
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-green-700 mt-2 text-center">
                            {t('studentAnswerTest.clickToModify')}
                          </p>

                          {/* Question en édition (dépliée sous les badges) */}
                          {editingQuestionId && answeredQuestions.find(q => q.id === editingQuestionId) && (() => {
                            const editingQuestion = answeredQuestions.find(q => q.id === editingQuestionId)!;
                            return (
                              <div className="mt-3 p-3 bg-white border-2 border-blue-400 rounded-lg">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-base font-bold text-gray-800">
                                    {t('studentAnswerTest.modifyQuestion')} Q{editingQuestion.question_number}
                                  </span>
                                  <button
                                    onClick={() => setEditingQuestionId(null)}
                                    className="text-xs text-gray-500 hover:text-gray-700"
                                  >
                                    {t('studentAnswerTest.close')}
                                  </button>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  {Array.from({ length: editingQuestion.number_of_answers }).map((_, answerIndex) => {
                                    const answerNumber = answerIndex + 1;
                                    const answerLetter = numberToLetter(answerNumber);
                                    const isSelected = studentAnswers[editingQuestion.id] === answerNumber;

                                    return (
                                      <button
                                        key={answerNumber}
                                        onClick={() => {
                                          handleAnswerSelect(editingQuestion.id, answerNumber);
                                          setEditingQuestionId(null);
                                        }}
                                        disabled={saving}
                                        className={`
                                          w-12 h-12 rounded-lg font-bold transition text-base
                                          ${isSelected
                                            ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg ring-2 ring-offset-2 ring-green-400'
                                            : 'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-700'
                                          }
                                          ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                                          touch-manipulation
                                        `}
                                      >
                                        {answerLetter}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bloc des questions passées (pliable) */}
                  {skippedQuestions.size > 0 && (
                    <div className="border-2 border-orange-300 bg-orange-50 rounded-lg overflow-hidden">
                      {/* Header cliquable avec chevron */}
                      <button
                        onClick={() => setShowSkippedQuestions(!showSkippedQuestions)}
                        className="w-full flex items-center justify-between p-3 hover:bg-orange-100 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-orange-800">
                            {t('studentAnswerTest.skippedQuestions')} ({skippedQuestions.size})
                          </span>
                        </div>
                        {showSkippedQuestions ? (
                          <ChevronUp className="w-5 h-5 text-orange-700" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-orange-700" />
                        )}
                      </button>

                      {/* Contenu pliable - Questions en badges horizontaux */}
                      {showSkippedQuestions && (
                        <div className="p-3 border-t border-orange-200">
                          <div className="flex flex-wrap gap-2">
                            {currentpartQuestions.filter(q => skippedQuestions.has(q.id)).map((q) => (
                              <button
                                key={q.id}
                                onClick={() => {
                                  const newSkipped = new Set(skippedQuestions);
                                  newSkipped.delete(q.id);
                                  setSkippedQuestions(newSkipped);
                                }}
                                className="inline-flex items-center gap-1 bg-white border-2 border-orange-400 rounded-lg px-3 py-2 hover:bg-orange-100 transition shadow-sm"
                              >
                                <span className="text-sm font-bold text-gray-700">
                                  Q{q.question_number}
                                </span>
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-orange-700 mt-2 text-center">
                            {t('studentAnswerTest.clickToAnswer')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Questions non répondues et non passées */}
                  {unansweredQuestions
                    .filter(q => !skippedQuestions.has(q.id))
                    .map((q) => (
                      <div
                        key={q.id}
                        className={`border-2 border-gray-300 bg-white rounded-lg p-4 transition-all duration-500 ${
                          animatingQuestionId === q.id
                            ? 'opacity-0 -translate-y-8 scale-95'
                            : 'opacity-100 translate-y-0 scale-100'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg font-bold text-gray-800">
                            {t('studentAnswerTest.question')} {q.question_number}
                          </span>
                          <button
                            onClick={async () => {
                              // Déclencher l'animation
                              setAnimatingQuestionId(q.id);

                              // Enregistrer le passage en base de données
                              await handleSkipQuestion(q.id);

                              // Attendre la fin de l'animation
                              setTimeout(() => {
                                setAnimatingQuestionId(null);
                              }, 500);
                            }}
                            disabled={saving}
                            className={`text-xs text-orange-600 hover:text-orange-800 font-medium bg-orange-50 hover:bg-orange-100 px-3 py-1 rounded transition ${
                              saving ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            {t('studentAnswerTest.skip')}
                          </button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {Array.from({ length: q.number_of_answers }).map((_, answerIndex) => {
                            const answerNumber = answerIndex + 1;
                            const answerLetter = numberToLetter(answerNumber);
                            const isSelected = studentAnswers[q.id] === answerNumber;

                            return (
                              <button
                                key={answerNumber}
                                onClick={() => handleAnswerSelect(q.id, answerNumber)}
                                disabled={saving}
                                className={`
                                  w-14 h-14 rounded-xl font-bold transition text-lg
                                  ${isSelected
                                    ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg scale-105'
                                    : 'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-700'
                                  }
                                  ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                                  touch-manipulation
                                `}
                              >
                                {answerLetter}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </>
              );
            })()}
          </div>
          )}

          {/* Vue Desktop: Tableau en 4 colonnes (≥ 768px) - Tests simples uniquement */}
          {test.type !== 'detailed' && (
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.answer')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.answer')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.answer')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('studentAnswerTest.answer')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Diviser les questions en 4 groupes égaux (seulement pour la part courante)
                  const quarterSize = Math.ceil(currentpartQuestions.length / 4);
                  const group1 = currentpartQuestions.slice(0, quarterSize);
                  const group2 = currentpartQuestions.slice(quarterSize, quarterSize * 2);
                  const group3 = currentpartQuestions.slice(quarterSize * 2, quarterSize * 3);
                  const group4 = currentpartQuestions.slice(quarterSize * 3);

                  // Nombre de lignes = taille du plus grand groupe
                  const maxRows = Math.max(group1.length, group2.length, group3.length, group4.length);

                  return Array.from({ length: maxRows }).map((_, rowIndex) => {
                    const q1 = group1[rowIndex];
                    const q2 = group2[rowIndex];
                    const q3 = group3[rowIndex];
                    const q4 = group4[rowIndex];

                    const renderQuestion = (q: typeof q1) => {
                      if (!q) {
                        return (
                          <>
                            <td className="border border-gray-300 px-2 py-2"></td>
                            <td className="border border-gray-300 px-2 py-2"></td>
                          </>
                        );
                      }

                      const isSkipped = studentAnswers[q.id] === -1 || skippedQuestions.has(q.id);
                      const isAnswered = studentAnswers[q.id] !== undefined && studentAnswers[q.id] !== -1;

                      return (
                        <>
                          <td className={`border border-gray-300 px-2 py-2 text-center font-semibold text-sm ${
                            isAnswered ? 'bg-green-50 text-green-700' : isSkipped ? 'bg-orange-50 text-orange-700' : 'text-gray-700'
                          }`}>
                            Q{q.question_number}
                          </td>
                          <td className={`border border-gray-300 px-2 py-2 ${
                            isAnswered ? 'bg-green-50' : isSkipped ? 'bg-orange-50' : ''
                          }`}>
                            <div className="flex flex-col gap-1 items-center">
                              <div className="flex gap-1 justify-center flex-wrap">
                                {Array.from({ length: q.number_of_answers }).map((_, answerIndex) => {
                                  const answerNumber = answerIndex + 1;
                                  const answerLetter = numberToLetter(answerNumber);
                                  const isSelected = studentAnswers[q.id] === answerNumber;
                                  const colorClass = isSelected ? answerColors[answerIndex % answerColors.length] : 'bg-gray-300 hover:bg-gray-400';

                                  return (
                                    <button
                                      key={answerNumber}
                                      onClick={() => handleAnswerSelect(q.id, answerNumber)}
                                      disabled={saving}
                                      className={`
                                        w-8 h-8 rounded-lg font-bold transition text-sm
                                        ${colorClass}
                                        ${isSelected ? 'text-white' : 'text-gray-700'}
                                        ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                                      `}
                                    >
                                      {answerLetter}
                                    </button>
                                  );
                                })}
                              </div>
                              {!isAnswered && !isSkipped && (
                                <button
                                  onClick={() => handleSkipQuestion(q.id)}
                                  disabled={saving}
                                  className={`text-xs text-orange-600 hover:text-orange-800 font-medium bg-orange-50 hover:bg-orange-100 px-2 py-0.5 rounded transition ${
                                    saving ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                  title={t('studentAnswerTest.skip')}
                                >
                                  Passer
                                </button>
                              )}
                              {isSkipped && (
                                <span className="text-xs text-orange-700 font-medium">
                                  Passée
                                </span>
                              )}
                            </div>
                          </td>
                        </>
                      );
                    };

                    return (
                      <tr
                        key={rowIndex}
                        className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-blue-50`}
                      >
                        {renderQuestion(q1)}
                        {renderQuestion(q2)}
                        {renderQuestion(q3)}
                        {renderQuestion(q4)}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          )}

          {/* Boutons de navigation */}
          <div className="flex justify-between items-center mt-6 sm:mt-8 gap-4">
            {/* Bouton Précédent */}
            {currentpartIndex > 0 && (
              <button
                onClick={handlePreviouspart}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 sm:px-8 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{t('studentAnswerTest.previousPart')}</span>
                <span className="sm:hidden">{t('studentAnswerTest.previous')}</span>
              </button>
            )}

            <div className="flex-1"></div>

            {/* Bouton Suivant ou Valider */}
            {hasNextpart ? (
              <button
                onClick={handleNextpart}
                disabled={!currentpartAnswersFilled}
                className={`flex items-center justify-center gap-2 font-semibold py-4 sm:py-3 px-4 sm:px-8 rounded-lg transition text-lg sm:text-base touch-manipulation ${
                  currentpartAnswersFilled
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span className="hidden sm:inline">{t('studentAnswerTest.nextPart')}</span>
                <span className="sm:hidden">{t('studentAnswerTest.next')}</span>
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </button>
            ) : (
              <button
                onClick={handleValidate}
                disabled={!canValidate}
                className={`flex items-center justify-center gap-2 font-semibold py-4 sm:py-3 px-4 sm:px-8 rounded-lg transition text-lg sm:text-base touch-manipulation ${
                  canValidate
                    ? 'bg-green-600 hover:bg-green-700 active:bg-green-800 text-white cursor-pointer shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Check className="w-5 h-5" />
                <span className="hidden sm:inline">{t('studentAnswerTest.validateAll')}</span>
                <span className="sm:hidden">{t('studentAnswerTest.validate')}</span>
                {canValidate && (
                  <span className="hidden sm:inline">
                    ({Object.keys(studentAnswers).length}/{questions.length})
                  </span>
                )}
              </button>
            )}
          </div>
            </>
          )}
        </div>
      </main>

      {/* Composant d'installation PWA */}
      <InstallPWA />
    </div>
  );
};

export default StudentAnswerTest;
